import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import { formatCompanyDateTime } from "@/lib/time";
import type { TaskWhatsApp } from "@/lib/types";

/** «Отправить в WhatsApp» for a driver with no Telegram: a plain link to
 * wa.me with the task text ready (built by the server). The owner sends it by
 * hand from WhatsApp — nothing is sent automatically; the tap only records
 * «отправлено в WhatsApp» with its time. */
export function WhatsAppSend({
  taskId,
  sentAt,
  invalidate,
}: {
  taskId: string;
  sentAt: string | null | undefined;
  invalidate: unknown[][];
}) {
  const { t } = useTranslation();
  const link = useQuery({
    queryKey: ["task-whatsapp", taskId],
    queryFn: () => api.get<TaskWhatsApp>(`/operational-tasks/${taskId}/whatsapp`),
    retry: false,
  });
  const mark = useEntityAction([...invalidate, ["task-whatsapp", taskId]], {
    successMessage: false,
  });

  if (link.isError) {
    return (
      <p className="text-xs text-sand-muted" data-testid="whatsapp-unavailable">
        {(link.error as Error).message}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="whatsapp-send">
      {link.data ? (
        <a
          href={link.data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md border px-2 py-1 font-medium"
          onClick={() =>
            mark.mutate(() => api.post(`/operational-tasks/${taskId}/whatsapp-sent`))
          }
        >
          {t("whatsapp.send")}
        </a>
      ) : (
        <span className="text-sand-muted">…</span>
      )}
      {sentAt && (
        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-700">
          {t("whatsapp.sent", { time: formatCompanyDateTime(sentAt) })}
        </span>
      )}
    </div>
  );
}
